import { cached, tracked } from '@glimmer/tracking';

import {
	BlockVisualization,
	ChartLegendPosition,
	DataSelectType,
	ShownLabels,
} from 'webapp/enums/enums';
import { uniq } from 'webapp/utils/common';
import { hashCode } from 'webapp/utils/number';

import type DataSelect from 'webapp/models-custom/data-select';
import DsDataOutputOtherLevel from 'webapp/models-custom/ds-data/output/other';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import VisualizationBase from 'webapp/models-custom/visualization/base';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import type { SeriesData } from 'webapp/d-chart/series/series';
import DChartSeries from 'webapp/d-chart/series/series';

type Output = DsDataOutputTopLevel | DsDataOutputOtherLevel;

const RecordLimit = 225;

export default class VisualizationSpider extends VisualizationBase {
	visualizationType = BlockVisualization.SPIDER;

	recordLimit = RecordLimit;

	get allowOthers() {
		return !!this.block.showOther;
	}

	get showValues() {
		return this.blockSetup.firstShownDataSelect?.shownLabels === ShownLabels.VALUE;
	}

	get isLegendOff(): boolean {
		return this.block.legendPosition === ChartLegendPosition.OFF;
	}

	get isLegendRight(): boolean {
		return this.block.legendPosition === ChartLegendPosition.RIGHT;
	}

	get isLegendBottom(): boolean {
		return !this.isLegendOff && !this.isLegendRight;
	}

	// ------ Data ------

	@tracked series: DChartSeries[] = [];

	override updateDataCache() {
		this.series = this.getSeries();
		this.updateColorIndexes();
	}

	override clearDataCache() {
		this.series = [];
	}

	updateColorIndexes() {
		this.updateColorIndexesForItems(this.series);
	}

	@cached
	get categories() {
		const outputs: Output[] = this.outputsByMetricAndUnit.reduce((acc, g) => {
			acc.push(...g.outputs);
			return acc;
		}, [] as Output[]);

		const categories = outputs.map((output) => {
			const isOther = output instanceof DsDataOutputOtherLevel;
			return isOther ? 'Other' : output.attributeNamesString;
		});

		return uniq(categories);
	}

	@cached
	private get outputsByMetricAndUnit() {
		interface OutputGroup {
			ds: DataSelect;
			unit: string;
			outputs: Output[];
		}

		const outputs: OutputGroup[] = [];

		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			const outputGroupsByUnit: OutputGroup[] = [];

			const allOutputs: Output[] = [...ds.outputs];
			if (this.allowOthers) {
				const otherOutputs = ds.dataRoot?.outputsOther || [];
				if (otherOutputs.length) {
					allOutputs.push(...otherOutputs);
				}
			}

			allOutputs.forEach((output) => {
				const existingGroup = outputGroupsByUnit.find((item) => item.unit === output.unit);
				if (existingGroup) {
					existingGroup.outputs.push(output);
				} else {
					outputGroupsByUnit.push({
						ds,
						unit: output.unit,
						outputs: [output], // these will be the points for one series
					});
				}
			});

			outputs.push(...outputGroupsByUnit);
		});

		return outputs;
	}

	private getSeries() {
		const series: DChartSeries[] = [];

		this.outputsByMetricAndUnit.forEach((group) => {
			const points: DChartPrimaryPoint[] = [];

			group.outputs.forEach((output) => {
				const isOther = output instanceof DsDataOutputOtherLevel,
					point = this.createDChartPointFromPoint(
						output,
						isOther ? output.pointTotal : output.point,
					);
				if (isOther) {
					point.metricSelectionData = Object.assign({}, points[0]?.metricSelectionData, {
						attribute: 'Other',
					});
				}
				points.push(point);
			});

			const ds = group.ds,
				unit = group.unit;

			const seriesData: SeriesData = {
				type: DataSelectType.MAIN,
				timezone: this.dataTimezone,
				title: group.outputs[0]?.titleWithoutAttribute || ds.shownTitle,
				points,
				showDataLabels: this.showValues,
				metricSelectionData: {
					dsId: ds.numericId!,
					primaryDsId: null,
					metricSetupData: ds.metricSetupData,
					metricName: ds.data?.metricName || '',
					dimensionValues: {}, // bcos different
					unit,
				},
			};

			const serie = new DChartSeries(seriesData);
			series.push(serie);
		});

		return series;
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const block = this.block;
		return hashCode(
			JSON.stringify({
				lPos: block.legendPosition,
				sw: this.showValues,
				bc: block.colors,
				o: this.allowOthers,
			}),
		);
	}

	// ------- MISC --------

	override getAllMainPoints() {
		return DChartSeries.getFirstPointsFromMainSeries(this.series);
	}

	static override recordLimit = RecordLimit;
}
